/**
 * 请求拦截器模块
 * 统一处理API请求、响应和登录状态
 */
import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios'
import { ElMessage } from 'element-plus'
import router, { isPublicRoute } from '@/baseUtils/router'
import { useUserStore } from '@/store/user'

// 定义响应数据类型
interface ApiResponse<T = any> {
	code?: number
	success?: boolean
	data: T
	message?: string
}

// 防抖计时器
let debounceTimer: ReturnType<typeof setTimeout> | null = null

// 环境变量中的API基础URL
const baseURL = import.meta.env.VITE_BASE_API as string

/**
 * 创建axios实例
 */
const service: AxiosInstance = axios.create({
	baseURL,
	timeout: 300000, // 请求超时时间 5分钟
	withCredentials: true
})

// 添加一个标记来防止重复处理登出
let isHandlingLogout = false

/**
 * 统一处理登出逻辑
 * @param message 显示给用户的消息
 * @param isPublic 当前路由是否公开
 */
export const handleLogout = (message: string, isPublic: boolean): void => {
	if (isHandlingLogout) return
	isHandlingLogout = true

	// 清除用户信息
	localStorage.removeItem('userInfo')
	localStorage.removeItem('userHash')
	localStorage.removeItem('loginState')
	localStorage.removeItem('userId')
	localStorage.removeItem('nickName')

	const userStore = useUserStore()
	userStore.clearUserInfo()

	if (userStore.loginState) {
		ElMessage({
			type: 'error',
			message
		})
		userStore.loginState = false
	}

	// 只在非公开路由时进行重定向
	if (!isPublic && router.currentRoute.value && router.currentRoute.value.path !== '/') {
		router.push('/').finally(() => {
			setTimeout(() => {
				isHandlingLogout = false
			}, 1000)
		})
	} else {
		isHandlingLogout = false
	}
}

/**
 * 判断当前路由是否为公开路由
 * @returns 当前路由是否公开
 */
export const isCurrentRoutePublic = (): boolean => {
	if (!router.currentRoute.value) return false

	// 检查路径是否在公开路由列表中
	if (isPublicRoute(router.currentRoute.value.path)) {
		return true
	}

	// 检查meta是否标记为公开
	return !!(router.currentRoute.value.meta && router.currentRoute.value.meta['public'] === true)
}

// 添加请求拦截器
service.interceptors.request.use(
	(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
		// 添加授权头
		if (config.headers) {
			config.headers['Authorization'] = 'Basic ' + btoa('admin:yayi!a@b#c')

			// 从localStorage读取locale并设置Accept-Language请求头
			const locale = localStorage.getItem('locale')
			if (locale === 'zh-CN' || !locale) {
				config.headers['Accept-Language'] = 'zh-CN;q=0.9'
			} else if (locale === 'en-US') {
				config.headers['Accept-Language'] = 'en-US;q=0.9'
			}
		}
		return config
	},
	(error: AxiosError): Promise<never> => {
		return Promise.reject(error)
	}
)

// 添加响应拦截器
service.interceptors.response.use(
	(response: AxiosResponse): any => {
		const res = response.data as ApiResponse
		const isPublic = isCurrentRoutePublic()
		console.log(isPublic)
		if (res.code && res.code !== 0) {
			if (res.code === 401 || res.code === 4001) {
				handleLogout('你已被登出，请重新登录', isPublic)
			}
			return Promise.reject(new Error('API请求异常: ' + (res.message || `错误码 ${res.code}`)))
		}
		return res
	},
	(error: AxiosError): Promise<never> => {
		const isPublic = isCurrentRoutePublic()
		if (error.response?.status === 401) {
			if (debounceTimer) return Promise.reject(error)

			debounceTimer = setTimeout(() => {
				debounceTimer = null
			}, 1000)

			handleLogout('会话过期，请重新登录', isPublic)
		} else if (error.message?.includes('timeout')) {
			ElMessage.error('网络请求超时')
		} else if (error.message === 'Network Error') {
			ElMessage.error('网络连接错误')
		} else if (error.response?.data) {
			ElMessage.error(error.response.statusText || '请求失败')
		} else {
			ElMessage.error('接口路径找不到')
		}

		return Promise.reject(error)
	}
)

export default service
